"use client";

import { useMemo, useState } from "react";

import { OrganizationLabel } from "@saas/account/ui/org/organization-label/organization-label";
import {
  BranchTypeEnum,
  IBranchTypeEnum,
} from "@saas/account/utils/@enum/org/branch-type/branch-type.enum";
import { useFlagsStatus } from "@saas/flags/utils/@hook/flags-status/use-flags-status";
import { NotificationContainer } from "@saas/notification/feature/notification-list/notification-container";
import { MenuIcon } from "@saas/shared/icon";
import { Divider } from "@saas/shared/ui/divider/divider";
import { Logo } from "@saas/shared/ui/logo/logo";
import { NewIndicator } from "@saas/shared/ui/new-indicator/new-indicator";
import { AppNameEnum } from "@saas/shared/utils/@enum/app-name/app-name.enum";
import { classNames } from "@saas/shared/utils/class-names/class-names";

import { useEventListener } from "usehooks-ts";

export interface TopNavigationBarProps {
  enabledNotificationR2?: boolean;
  showIndicator?: Record<string, boolean>;
  variantLogo?: AppNameEnum;
  accountMenu: () => JSX.Element;
  handleShowAside?: () => void;

  // New props for organization
  organizationName?: string;
  branchName?: string;
  branchType?: IBranchTypeEnum;
}

// TODO: move this to feature folder
export const TopNavigationBar = ({
  enabledNotificationR2,
  showIndicator,
  variantLogo,
  accountMenu: AccountMenu,
  handleShowAside,
  organizationName,
  branchName = "",
  branchType,
}: TopNavigationBarProps) => {
  const { organization: organizationFlag } = useFlagsStatus();

  const [sticky, setSticky] = useState(false);

  const isMelakaApp = variantLogo === AppNameEnum.MELAKA;

  const branchLabel =
    branchType === BranchTypeEnum.HEADQUARTER ? `Pusat` : branchName;

  const showOrganizationLabel =
    !!organizationName && !!branchName && organizationFlag;

  const isHasNewMenu = useMemo(() => {
    if (!showIndicator) return false;
    return Object.values(showIndicator).some((indicator) => indicator);
  }, [showIndicator]);

  useEventListener("scroll", () => {
    window.scrollY > 0 ? setSticky(true) : setSticky(false);
  });

  return (
    <header
      className={classNames(
        "bg-neutral-N0 z-[5] flex flex-shrink-0 flex-row items-center justify-between gap-4 px-5 py-4 md:px-6 md:py-2",
        sticky && "shadow-elevation-1 sticky top-0"
      )}
    >
      <div className={"mr-auto flex items-center gap-4 md:hidden"}>
        {handleShowAside ? (
          <button
            onClick={handleShowAside}
            data-testid={"home__icon__hamburger-menu"}
            className={"flex"}
          >
            <MenuIcon />
            {isHasNewMenu ? (
              <NewIndicator
                size={"small"}
                className={"absolute top-0 right-2"}
                testid={"journal__icon__indicator-hamburger"}
              />
            ) : null}
          </button>
        ) : null}

        {variantLogo === AppNameEnum.MELAKA_POS ? (
          <Logo variant={"pos"} />
        ) : null}

        {isMelakaApp ? <Logo className={"h-7 w-full"} /> : null}
      </div>

      <div className={"ml-auto flex flex-row items-center justify-end gap-3"}>
        {showOrganizationLabel ? (
          <div className={"mr-3"}>
            <OrganizationLabel
              organizationName={organizationName}
              branchName={branchLabel}
            />
          </div>
        ) : null}

        <NotificationContainer enabledNotificationR2={enabledNotificationR2} />
        <Divider
          className={"min-h-[24px] md:min-h-[42px]"}
          orientation={"vertical"}
        />

        <AccountMenu />
      </div>
    </header>
  );
};

export default TopNavigationBar;
